import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePermissions } from '@/shared/platform';
import {
  FilterBar,
  NumberedPager,
  ListEmpty,
  MoreInOtherMonths,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  DEFAULT_HISTORY_PRESET,
  type HistoryDatePreset,
} from '@/components/lists';
import type { WriteOffReason, WriteOffRow } from '../types';
import { money, WRITE_OFF_REASONS } from '../utils';
import { generateWrittenOffHistory } from '../historyMock';
import { PMS } from '@/features/shared/pms';

interface Props {
  rows: WriteOffRow[];
  total: number;
  count: number;
  onAddReason: (id: string, reason: WriteOffReason, note?: string) => void;
}

// "Add a reason" records WHY a write-off already made won't be collected from
// insurance, so it never offers to write it off again (staging fix §7). It
// draws from the ONE shared reason list, minus the write-it-off action.
const ADD_REASONS = WRITE_OFF_REASONS.filter((r) => r.id !== 'write_off');

const PAGE_SIZE = 50;

function AddReasonDialog({ row, onCancel, onConfirm }: { row: WriteOffRow; onCancel: () => void; onConfirm: (reason: WriteOffReason, note?: string) => void }) {
  const [reason, setReason] = useState<WriteOffReason>(ADD_REASONS[0]?.id ?? 'patient_pays');
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);
  const needsNote = reason === 'something_else';
  const valid = !needsNote || note.trim() !== '';
  return (
    <div className="iar-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div ref={ref} className="iar-dialog" role="dialog" aria-modal="true" aria-labelledby="iar-ar-h">
        <h2 id="iar-ar-h" className="iar-dialog-title">Why was {row.patient}’s claim written off?</h2>
        <div className="iar-notice">This records a reason for the write-off {row.by}. It doesn’t change the write-off itself.</div>
        <fieldset className="iar-radios">
          <legend className="iar-legend">Reason</legend>
          {ADD_REASONS.map((r) => (
            <label key={r.id} className="iar-opt">
              <input type="radio" name="iar-addreason" checked={reason === r.id} onChange={() => { setReason(r.id); }} />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>
        {needsNote ? (
          <label className="iar-flabel"><span>Note</span><input className="iar-field" value={note} onChange={(e) => { setNote(e.target.value); }} /></label>
        ) : null}
        <div className="iar-dialog-actions">
          <button type="button" className="iar-btn iar-btn-primary" disabled={!valid} onClick={() => { onConfirm(reason, note.trim() || undefined); }}>Save reason</button>
          <button type="button" className="iar-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/** The reason as a short, filterable label ("Not given" when the PMS gave none). */
function reasonOf(r: WriteOffRow): string {
  return r.why === null ? 'Not given' : (r.why.split('. ')[0] ?? r.why);
}

const csvEsc = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

function recordHref(r: WriteOffRow): string {
  return `/claims/record/${encodeURIComponent(r.recordId ?? r.id)}`;
}

/**
 * Written off — history (00-ds-09 §9): a filter bar (search · date range ·
 * payer) over ~400 written-off claims across ~24 months, numbered pages of 50,
 * default this month, each row opening the claim record. UI-only over mock data
 * (§8); the live store rows (this month) sit on top of the generated history.
 */
export function WrittenOff({ rows, total, count, onAddReason }: Props) {
  const [addFor, setAddFor] = useState<WriteOffRow | null>(null);
  const navigate = useNavigate();
  const { role } = usePermissions();
  // Export CSV is for owners and admins only; each export is logged (D152).
  const canExport = role !== 'staff' && role !== 'provider';
  const [urlState, patch] = useListUrlState({ q: '', range: DEFAULT_HISTORY_PRESET, payer: '', reason: '', page: '1' });

  const query = urlState.q;
  const preset = urlState.range as HistoryDatePreset;
  const payer = urlState.payer;
  const reason = urlState.reason;
  const page = Math.max(1, Number.parseInt(urlState.page, 10) || 1);

  // Live store rows (this month) layered over the generated history.
  const all = useMemo(() => [...rows, ...generateWrittenOffHistory()], [rows]);

  const payerOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of all) s.add(r.payer);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [all]);

  const reasonOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of all) set.add(reasonOf(r));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [all]);

  const textOf = (r: WriteOffRow) =>
    `${r.patient} ${r.payer} ${r.why ?? ''} ${String(r.amount)} ${money(r.amount)} ${r.recordId ?? r.id}`;

  // search + payer first (window-independent), so we can count "other months".
  const matched = useMemo(() => {
    const byPayer = payer === '' ? all : all.filter((r) => r.payer === payer);
    const byReason = reason === '' ? byPayer : byPayer.filter((r) => reasonOf(r) === reason);
    return applySearch(byReason, query, textOf);
  }, [all, payer, reason, query]);

  const range = rangeForPreset(preset);
  const inWindow = useMemo(
    () => sortBy(matched.filter((r) => inRange(r.iso ?? '', range)), (r) => r.iso ?? '', -1),
    [matched, range],
  );
  const outsideWindow = preset === 'all-time' ? 0 : matched.length - inWindow.length;

  const pageRows = paginate(inWindow, page, PAGE_SIZE);
  const label = windowLabel(preset);

  const exportCsv = () => {
    const header = ['Date', 'Patient', 'Visit', 'Payer', 'Why', 'By', 'Amount'];
    const lines = inWindow.map((r) =>
      [r.dateDisplay, r.patient, r.visit, r.payer, r.why ?? 'Not given', r.by, r.amount.toFixed(2)].map(csvEsc).join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `written-off-${String(inWindow.length)}-rows.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* mock */
    }
    // Every export is logged (D152).
    console.log(`Export logged: ${String(inWindow.length)} written-off rows exported by ${role ?? 'current user'}.`);
  };

  const setPreset = (v: HistoryDatePreset) => { patch({ range: v, page: '1' }); };

  return (
    <main className="iar-wide">
      {/* D146: the "Before ELVA" baseline figure has been removed. */}
      <div className="iar-figgrid">
        <section className="iar-card iar-fig">
          <span className="iar-meta">Insurance money written off, October so far</span>
          <span className="v num">{money(total)}</span>
          <span className="iar-meta">{count} {count === 1 ? 'claim' : 'claims'}</span>
        </section>
      </div>

      <section className="iar-card iar-status" aria-labelledby="iar-wl">
        <div style={{ padding: '16px 18px 8px' }}>
          <h2 id="iar-wl" className="iar-h3" style={{ fontSize: 17 }}>Written off</h2>
          <p className="iar-meta" style={{ marginTop: 2 }}>Showing {label}.</p>
        </div>
        <div style={{ padding: '0 14px 6px' }}>
          <FilterBar
            search={query}
            onSearch={(v) => { patch({ q: v, page: '1' }); }}
            searchPlaceholder="Search patient, payer, claim or amount"
            dateRange={preset}
            onDateRange={setPreset}
            {...(canExport ? { right: <button type="button" className="iar-btn iar-btn-sm" onClick={exportCsv}>Export CSV</button> } : {})}
          >
            <select
              className="lx-select"
              value={payer}
              aria-label="Payer"
              onChange={(e) => { patch({ payer: e.target.value, page: '1' }); }}
            >
              <option value="">All payers</option>
              {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              className="lx-select"
              value={reason}
              aria-label="Reason"
              onChange={(e) => { patch({ reason: e.target.value, page: '1' }); }}
            >
              <option value="">Any reason</option>
              {reasonOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </FilterBar>
        </div>

        {inWindow.length === 0 ? (
          <div style={{ padding: '4px 14px 10px' }}>
            <ListEmpty
              windowLabel={label}
              {...(query.trim() !== '' ? { query: query.trim() } : {})}
              {...(query.trim() !== '' && outsideWindow > 0 ? { onSearchAllTime: () => { setPreset('all-time'); } } : {})}
              {...(query.trim() === '' ? { message: `Nothing was written off in ${label}.` } : {})}
            />
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="iar-tbl">
                <thead>
                  <tr>
                    <th scope="col">Date</th><th scope="col">Claim</th><th scope="col">Payer</th><th scope="col">Why</th><th scope="col">By</th><th scope="col" className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => { void navigate(recordHref(r)); }}>
                      <td className="num iar-meta" style={{ whiteSpace: 'nowrap' }}>{r.dateDisplay}</td>
                      <td>
                        <Link to={recordHref(r)} onClick={(e) => { e.stopPropagation(); }} className="iar-src" style={{ textDecorationStyle: 'solid', fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>{r.patient}</Link>
                        <div className="iar-meta">{r.visit}</div>
                      </td>
                      <td className="iar-meta">{r.payer}</td>
                      <td>
                        {r.why ?? (
                          <>Not given. <button type="button" className="iar-src" onClick={(e) => { e.stopPropagation(); setAddFor(r); }}>Add a reason</button></>
                        )}
                      </td>
                      <td className="iar-meta" style={{ fontSize: 13.5 }}>{r.by}</td>
                      <td className="r num"><strong>{money(r.amount)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '2px 14px 8px' }}>
              <MoreInOtherMonths
                count={query.trim() !== '' ? outsideWindow : 0}
                onShowAllTime={() => { setPreset('all-time'); }}
              />
              <NumberedPager
                page={page}
                pageSize={PAGE_SIZE}
                total={inWindow.length}
                onPage={(p) => { patch({ page: String(p) }); }}
              />
            </div>
          </>
        )}

        <div className="iar-meta" style={{ padding: '10px 18px 14px', borderTop: '1px solid var(--border)' }}>
          Includes write-offs made directly in ${PMS}; ELVA reads them and asks for a reason if none was given. Contractual write-offs (the part of your fee a PPO doesn’t allow) are normal and not counted here.
        </div>
      </section>

      {addFor ? (
        <AddReasonDialog
          row={addFor}
          onCancel={() => { setAddFor(null); }}
          onConfirm={(reason, note) => { onAddReason(addFor.id, reason, note); setAddFor(null); }}
        />
      ) : null}
    </main>
  );
}
